import { useQuery, useMutation } from '@tanstack/react-query'
import { getDoctorBrief, shareBrief, createAppointment, runPLN, runMonitor } from '../lib/api'
import { getMe } from '../lib/api'
import { useState } from 'react'
import { FileText, Share2, Calendar, Brain, RefreshCw } from 'lucide-react'

// Minimal markdown renderer — handles ##/### headings, **bold**, bullet lists
function BriefMarkdown({ text }: { text: string }) {
  const lines = text.split('\n')
  return (
    <div className="prose prose-sm max-w-none text-gray-700">
      {lines.map((line, i) => {
        if (line.startsWith('### '))
          return <h3 key={i} className="text-sm font-semibold text-gray-800 mt-4 mb-1">{line.slice(4)}</h3>
        if (line.startsWith('## '))
          return <h2 key={i} className="text-base font-bold text-gray-900 mt-5 mb-2 border-b border-gray-100 pb-1">{line.slice(3)}</h2>
        if (line.startsWith('- ') || line.startsWith('* ')) {
          const content = line.slice(2)
          return (
            <li key={i} className="ml-4 list-disc text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: boldify(content) }} />
          )
        }
        if (line.trim() === '') return <div key={i} className="h-2" />
        return (
          <p key={i} className="text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: boldify(line) }} />
        )
      })}
    </div>
  )
}

function boldify(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
}

export default function DoctorBrief() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => getMe().then(r => r.data) })
  const personId = me?.id

  const {
    data: brief,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['doctor-brief'],
    queryFn: () => getDoctorBrief(personId!).then(r => r.data),
    enabled: !!personId,
    staleTime: 1000 * 60 * 5,
  })

  const share = useMutation({ mutationFn: () => shareBrief(7) })

  const [showAppt, setShowAppt] = useState(false)
  const [apptDate, setApptDate] = useState('')
  const [doctorName, setDoctorName] = useState('')

  const bookAppt = useMutation({
    mutationFn: () => createAppointment({ scheduled_at: new Date(apptDate).toISOString(), doctor_name: doctorName }),
    onSuccess: () => { setShowAppt(false); setApptDate(''); setDoctorName('') },
  })

  // Regenerate: run reasoning then refetch brief
  const regen = useMutation({
    mutationFn: async () => {
      await runPLN()
      await runMonitor()
    },
    onSuccess: () => refetch(),
  })

  if (!personId || isLoading) return (
    <div className="p-6 flex items-center gap-2 text-sm text-gray-400">
      <RefreshCw size={14} className="animate-spin" /> Generating brief…
    </div>
  )

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Doctor Brief</h2>
          <p className="text-sm text-gray-500">
            AI-generated from your health data · GPT-OSS 120B
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => regen.mutate()}
            disabled={regen.isPending || isFetching}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {regen.isPending || isFetching
              ? <RefreshCw size={14} className="animate-spin" />
              : <Brain size={14} />}
            Regenerate
          </button>
          <button
            onClick={() => setShowAppt(!showAppt)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
          >
            <Calendar size={14} /> Schedule visit
          </button>
          <button
            onClick={() => share.mutate()}
            disabled={share.isPending || !brief}
            className="flex items-center gap-2 px-3 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 disabled:opacity-50"
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      {/* Share confirmation */}
      {share.data && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-700">
          Share link created. Expires {new Date(share.data.data.expires_at).toLocaleDateString()}.{' '}
          Token: <code className="font-mono">{share.data.data.share_token}</code>
        </div>
      )}

      {/* Schedule appointment */}
      {showAppt && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Schedule appointment</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date & time</label>
              <input type="datetime-local" value={apptDate} onChange={e => setApptDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Doctor name (optional)</label>
              <input type="text" value={doctorName} onChange={e => setDoctorName(e.target.value)}
                placeholder="Dr. Kim"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => bookAppt.mutate()} disabled={!apptDate || bookAppt.isPending}
              className="px-4 py-1.5 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 disabled:opacity-50">
              Book
            </button>
            <button onClick={() => setShowAppt(false)}
              className="px-4 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* No data state */}
      {(isError || !brief) && !isLoading && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
          <Brain size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-3">
            No brief available yet. Add health data and run reasoning first.
          </p>
          <button
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {regen.isPending ? 'Running…' : 'Run Reasoning & Generate'}
          </button>
        </div>
      )}

      {/* Brief content */}
      {brief?.brief && (
        <>
          {/* Metadata strip */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-400 mb-4">
            {brief.chains_included != null && <span>{brief.chains_included} causal chains</span>}
            {brief.alerts_included != null && <span>{brief.alerts_included} active alerts</span>}
            {brief.atom_types_included != null && <span>{brief.atom_types_included} data types</span>}
          </div>

          {/* LLM-generated markdown brief */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-5">
            <BriefMarkdown text={brief.brief} />
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500">
            <FileText size={12} className="inline mr-1" />
            This brief is generated by an AI reasoning engine and is not a medical diagnosis.
            It is intended to support — not replace — a conversation with your doctor.
            Always consult a qualified healthcare professional before making medical decisions.
          </div>
        </>
      )}
    </div>
  )
}
